<template>
  <b-form class="account-setting-password" @submit.prevent="submitPassword">
    <b-alert
      v-if="formError"
      variant="danger"
      show
      dismissible
      class="py-75 mb-1"
      @dismissed="formError = ''"
    >
      <div class="d-flex align-items-start">
        <feather-icon icon="AlertCircleIcon" size="16" class="mr-50 flex-shrink-0 mt-25" />
        <span class="small">{{ formError }}</span>
      </div>
    </b-alert>

    <b-alert
      v-if="formSuccess"
      variant="success"
      show
      dismissible
      class="py-75 mb-1"
      @dismissed="formSuccess = ''"
    >
      <div class="d-flex align-items-start">
        <feather-icon icon="CheckCircleIcon" size="16" class="mr-50 flex-shrink-0 mt-25" />
        <span class="small">{{ formSuccess }}</span>
      </div>
    </b-alert>

    <b-row>
      <b-col cols="12">
        <b-form-group
          label="Current password"
          label-for="account-old-password"
          label-class="font-weight-bold small"
          class="mb-1"
        >
          <b-input-group class="input-group-merge">
            <b-form-input
              id="account-old-password"
              v-model="oldPassword"
              name="current-password"
              autocomplete="current-password"
              :type="fieldType.old"
              :state="fieldState('old')"
              placeholder="Enter current password"
              :disabled="saving"
              @blur="touched.old = true"
              @keyup.enter="submitPassword"
            />
            <b-input-group-append is-text>
              <feather-icon
                :icon="toggleIcon('old')"
                class="cursor-pointer"
                role="button"
                tabindex="0"
                aria-label="Toggle current password visibility"
                @click="toggleField('old')"
                @keydown.enter.prevent="toggleField('old')"
                @keydown.space.prevent="toggleField('old')"
              />
            </b-input-group-append>
          </b-input-group>
          <b-form-invalid-feedback :state="fieldState('old')">
            Current password is required.
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>

      <b-col cols="12">
        <hr class="account-setting-password__divider my-75" />
      </b-col>

      <b-col cols="12">
        <b-form-group
          label="New password"
          label-for="account-new-password"
          label-class="font-weight-bold small"
          description="At least 8 characters. Do not reuse your current password."
          class="mb-1"
        >
          <b-input-group class="input-group-merge">
            <b-form-input
              id="account-new-password"
              v-model="newPassword"
              name="new-password"
              autocomplete="new-password"
              :type="fieldType.new"
              :state="fieldState('new')"
              placeholder="Enter new password"
              :disabled="saving"
              @blur="touched.new = true"
            />
            <b-input-group-append is-text>
              <feather-icon
                :icon="toggleIcon('new')"
                class="cursor-pointer"
                role="button"
                tabindex="0"
                aria-label="Toggle new password visibility"
                @click="toggleField('new')"
                @keydown.enter.prevent="toggleField('new')"
                @keydown.space.prevent="toggleField('new')"
              />
            </b-input-group-append>
          </b-input-group>
          <b-form-invalid-feedback :state="fieldState('new')">
            {{ newPasswordError }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>

      <b-col cols="12">
        <b-form-group
          label="Confirm new password"
          label-for="account-retype-new-password"
          label-class="font-weight-bold small"
          class="mb-1"
        >
          <b-input-group class="input-group-merge">
            <b-form-input
              id="account-retype-new-password"
              v-model="confirmPassword"
              name="confirm-password"
              autocomplete="new-password"
              :type="fieldType.confirm"
              :state="fieldState('confirm')"
              placeholder="Re-enter new password"
              :disabled="saving"
              @blur="touched.confirm = true"
            />
            <b-input-group-append is-text>
              <feather-icon
                :icon="toggleIcon('confirm')"
                class="cursor-pointer"
                role="button"
                tabindex="0"
                aria-label="Toggle confirm password visibility"
                @click="toggleField('confirm')"
                @keydown.enter.prevent="toggleField('confirm')"
                @keydown.space.prevent="toggleField('confirm')"
              />
            </b-input-group-append>
          </b-input-group>
          <b-form-invalid-feedback :state="fieldState('confirm')">
            {{ confirmPasswordError }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>

      <b-col cols="12">
        <div class="d-flex flex-wrap align-items-center account-setting-password__actions mt-50">
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            type="submit"
            variant="primary"
            class="mr-75 mb-50 mb-sm-0"
            :disabled="saving || !canSubmit"
          >
            <b-spinner v-if="saving" small class="mr-25" />
            <feather-icon v-else icon="SaveIcon" size="14" class="mr-25" />
            {{ saving ? 'Saving…' : 'Update password' }}
          </b-button>
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            type="button"
            variant="outline-secondary"
            :disabled="saving || !hasAnyValue"
            @click="clearForm"
          >
            Clear
          </b-button>
        </div>
      </b-col>
    </b-row>
  </b-form>
</template>

<script>
import {
  BAlert,
  BButton,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BInputGroup,
  BInputGroupAppend,
  BRow,
  BSpinner,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import userService from '@/services/user-service'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

const MIN_PASSWORD_LENGTH = 8

function extractErrorMessage(res) {
  if (!res) return 'Unable to update password. Please try again.'
  if (typeof res === 'string') return res
  const data = res.data && typeof res.data === 'object' ? res.data : res
  return (
    data.message
    || data.error
    || (data.data && data.data.message)
    || (typeof res.statusText === 'string' && res.statusText)
    || 'Unable to update password. Please try again.'
  )
}

function isSuccessResponse(res) {
  if (!res || typeof res !== 'object') return false
  const status = res.status
  return status === 'OK' || status === 'success' || status === 200
}

export default {
  name: 'AccountSettingPassword',
  components: {
    BAlert,
    BButton,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BInputGroup,
    BInputGroupAppend,
    BRow,
    BSpinner,
  },
  directives: {
    Ripple,
  },
  props: {
    userEmail: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
      fieldType: {
        old: 'password',
        new: 'password',
        confirm: 'password',
      },
      touched: {
        old: false,
        new: false,
        confirm: false,
      },
      saving: false,
      formError: '',
      formSuccess: '',
      submitted: false,
    }
  },
  computed: {
    hasAnyValue() {
      return !!(this.oldPassword || this.newPassword || this.confirmPassword)
    },
    newPasswordError() {
      if (!this.newPassword) return 'New password is required.'
      if (this.newPassword.length < MIN_PASSWORD_LENGTH) {
        return `New password must be at least ${MIN_PASSWORD_LENGTH} characters.`
      }
      if (this.oldPassword && this.newPassword === this.oldPassword) {
        return 'New password must be different from your current password.'
      }
      return ''
    },
    confirmPasswordError() {
      if (!this.confirmPassword) return 'Please confirm your new password.'
      if (this.newPassword !== this.confirmPassword) return 'Passwords do not match.'
      return ''
    },
    canSubmit() {
      return !!(
        this.oldPassword
        && this.newPassword
        && this.confirmPassword
        && !this.newPasswordError
        && !this.confirmPasswordError
      )
    },
  },
  methods: {
    toggleIcon(field) {
      return this.fieldType[field] === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
    toggleField(field) {
      this.fieldType[field] = this.fieldType[field] === 'password' ? 'text' : 'password'
    },
    fieldState(field) {
      if (!this.submitted && !this.touched[field]) return null
      if (field === 'old') return this.oldPassword ? null : false
      if (field === 'new') return this.newPasswordError ? false : (this.newPassword ? true : false)
      if (field === 'confirm') return this.confirmPasswordError ? false : (this.confirmPassword ? true : false)
      return null
    },
    showToast(title, variant = 'success') {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon: variant === 'success' ? 'CheckIcon' : 'AlertCircleIcon',
          variant,
        },
      })
    },
    clearForm() {
      this.oldPassword = ''
      this.newPassword = ''
      this.confirmPassword = ''
      this.fieldType = { old: 'password', new: 'password', confirm: 'password' }
      this.touched = { old: false, new: false, confirm: false }
      this.submitted = false
      this.formError = ''
      // Keep success message if just succeeded; clear otherwise
      if (!this.formSuccess) this.formSuccess = ''
    },
    resolveEmail() {
      if (this.userEmail) return this.userEmail
      try {
        const userData = JSON.parse(localStorage.getItem('userData') || '{}')
        return (userData && userData.email) || ''
      } catch (e) {
        return ''
      }
    },
    async submitPassword() {
      this.submitted = true
      this.touched = { old: true, new: true, confirm: true }
      this.formError = ''
      this.formSuccess = ''

      if (!this.canSubmit) {
        this.formError = this.newPasswordError
          || this.confirmPasswordError
          || 'Please fill in all password fields.'
        return
      }

      const email = this.resolveEmail()
      if (!email) {
        this.formError = 'Could not determine your account email. Please sign in again.'
        this.showToast(this.formError, 'danger')
        return
      }

      this.saving = true
      try {
        const res = await userService.resetPassword({
          password: this.oldPassword,
          new_password: this.newPassword,
          email,
        })

        if (isSuccessResponse(res)) {
          // Inline success banner only — avoid duplicate toast
          this.formSuccess = 'Password updated successfully.'
          this.oldPassword = ''
          this.newPassword = ''
          this.confirmPassword = ''
          this.fieldType = { old: 'password', new: 'password', confirm: 'password' }
          this.touched = { old: false, new: false, confirm: false }
          this.submitted = false
        } else {
          this.formError = extractErrorMessage(res)
          this.showToast(this.formError, 'danger')
        }
      } catch (e) {
        this.formError = extractErrorMessage(e && e.response ? e.response : e)
        this.showToast(this.formError, 'danger')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.account-setting-password__divider {
  border-top-color: #ebe9f1;
}
.account-setting-password__actions .btn {
  min-width: 8.5rem;
}
@media (max-width: 575.98px) {
  .account-setting-password__actions .btn {
    width: 100%;
    margin-right: 0 !important;
  }
  .account-setting-password__actions .btn + .btn {
    margin-top: 0.5rem;
  }
}
</style>
